import { extractCollection, extractPayload } from '@/utils/dataNormalization';

export const DEFAULT_REPORTS_TOOLBAR_KEY = 'sales';
export const REPORTS_SORT_DIRECTION_NONE = '';
export const REPORTS_SORT_DIRECTION_ASC = 'asc';
export const REPORTS_SORT_DIRECTION_DESC = 'desc';

const SORT_ICON_BY_DIRECTION = {
  [REPORTS_SORT_DIRECTION_NONE]: 'fa-sort',
  [REPORTS_SORT_DIRECTION_ASC]: 'fa-sort-amount-asc',
  [REPORTS_SORT_DIRECTION_DESC]: 'fa-sort-amount-desc',
};

const SELECTOR_CODE_TO_KEY = {
  columns: 'columns',
  periods: 'periods',
  values: 'values',
  customers: 'customers',
  sellers: 'seller',
  managers: 'owner',
  sales: 'sales',
  sale_statuses: 'status',
  service_types: 'serviceType',
  products: 'product',
  tags: 'tag',
  areas: 'area',
  currencies: 'currency',
  display_currency: 'displayCurrency',
  invoiced: 'invoiced',
  receipt_type: 'receiptType',
  comprobante: 'receiptType',
  show_in: 'displayCurrency',
};

const normalizeCode = (code = '') => String(code || '').trim();

export const normalizeReportsSortDirection = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();

  return [REPORTS_SORT_DIRECTION_ASC, REPORTS_SORT_DIRECTION_DESC].includes(normalized)
    ? normalized
    : REPORTS_SORT_DIRECTION_NONE;
};

export const normalizeReportsToolbarFiltersPayload = (payload = {}) => {
  const source = payload && typeof payload === 'object' ? payload : {};

  return {
    ...source,
    sort_direction: normalizeReportsSortDirection(source.sort_direction),
  };
};

export const stripReportsSortDirectionFromPayload = (payload = {}) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const withoutSort = { ...source };
  delete withoutSort.sort_direction;
  return withoutSort;
};

export const hasReportsToolbarNonSortChanges = (nextPayload, previousPayload) => {
  if (!previousPayload || typeof previousPayload !== 'object') {
    return true;
  }

  const normalizedNext = stripReportsSortDirectionFromPayload(nextPayload);
  const normalizedPrevious = stripReportsSortDirectionFromPayload(previousPayload);

  return JSON.stringify(normalizedNext) !== JSON.stringify(normalizedPrevious);
};

export const resolveReportsToolbarSortChange = ({
  payload = {},
  meta = {},
  currentFilters = null,
} = {}) => {
  const normalizedPayload = normalizeReportsToolbarFiltersPayload(payload);
  const trigger = String(meta?.trigger || '').trim().toLowerCase();
  const baseFilters = currentFilters && typeof currentFilters === 'object'
    ? currentFilters
    : {};

  return {
    normalizedPayload,
    sortDirection: normalizedPayload.sort_direction,
    nextFilters: trigger === 'sort'
      ? {
        ...baseFilters,
        sort_direction: normalizedPayload.sort_direction,
      }
      : currentFilters,
  };
};

export const buildReportsSortDirectionOptions = (sortText = {}) => {
  const options = sortText && typeof sortText === 'object' ? sortText.options : {};

  return [
    {
      value: REPORTS_SORT_DIRECTION_NONE,
      icon: SORT_ICON_BY_DIRECTION[REPORTS_SORT_DIRECTION_NONE],
      label: String(options?.none || '').trim(),
    },
    {
      value: REPORTS_SORT_DIRECTION_ASC,
      icon: SORT_ICON_BY_DIRECTION[REPORTS_SORT_DIRECTION_ASC],
      label: String(options?.asc || '').trim(),
    },
    {
      value: REPORTS_SORT_DIRECTION_DESC,
      icon: SORT_ICON_BY_DIRECTION[REPORTS_SORT_DIRECTION_DESC],
      label: String(options?.desc || '').trim(),
    },
  ];
};

export const resolveReportsSortButtonIcon = (sortDirection = REPORTS_SORT_DIRECTION_NONE) => {
  const normalizedDirection = normalizeReportsSortDirection(sortDirection);
  return SORT_ICON_BY_DIRECTION[normalizedDirection] || SORT_ICON_BY_DIRECTION[REPORTS_SORT_DIRECTION_NONE];
};

export const resolveReportsSortButtonTitle = ({
  sortDirection = REPORTS_SORT_DIRECTION_NONE,
  options = [],
  prefix = '',
} = {}) => {
  const normalizedDirection = normalizeReportsSortDirection(sortDirection);
  const normalizedPrefix = String(prefix || '').trim();
  const selectedOption = (Array.isArray(options) ? options : []).find((option) => {
    return String(option?.value || '') === normalizedDirection;
  });
  const selectedLabel = String(selectedOption?.label || '').trim();

  if (normalizedPrefix === '' || selectedLabel === '') {
    return `${normalizedPrefix}${selectedLabel}`.trim();
  }

  return `${normalizedPrefix}: ${selectedLabel}`;
};

export const isReportsEventOutsideElement = (event, element) => {
  if (!element || typeof element.contains !== 'function') {
    return false;
  }

  return !element.contains(event?.target);
};

export const mapSelectorCodeToKey = (code = '') => {
  const normalizedCode = normalizeCode(code);
  return SELECTOR_CODE_TO_KEY[normalizedCode] || normalizedCode;
};

export const normalizeReportsSelectorDefinitions = (rawDefinitions = []) => {
  return (Array.isArray(rawDefinitions) ? rawDefinitions : [])
    .map((definition) => {
      const code = normalizeCode(definition?.code);
      if (code === '') {
        return null;
      }

      const key = mapSelectorCodeToKey(code);
      const name = String(definition?.name || key).trim() || key;
      const group = String(definition?.group || 'top').trim() || 'top';
      const individual = Number(definition?.individual ?? -1);
      const multiple = Number(definition?.multiple ?? 1);

      return {
        code,
        key,
        name,
        group,
        individual,
        multiple,
        defaultValue: String(definition?.default_value ?? '').trim(),
      };
    })
    .filter(Boolean);
};

export const mergeReportsSelectorDefinitions = (baseDefinitions = [], localDefinitions = []) => {
  const merged = [];
  const usedKeys = new Set();

  const appendDefinition = (definition) => {
    if (!definition || typeof definition !== 'object') {
      return;
    }

    const key = normalizeCode(definition.key);
    if (key === '' || usedKeys.has(key)) {
      return;
    }

    usedKeys.add(key);
    merged.push(definition);
  };

  (Array.isArray(baseDefinitions) ? baseDefinitions : []).forEach(appendDefinition);
  (Array.isArray(localDefinitions) ? localDefinitions : []).forEach(appendDefinition);

  return merged;
};

export const resolveReportsLocalSelectorOptions = (localSelectorOptions, selectorCode, key = '') => {
  const source = localSelectorOptions && typeof localSelectorOptions === 'object'
    ? localSelectorOptions
    : {};

  const normalizedCode = normalizeCode(selectorCode);
  const normalizedKey = normalizeCode(key);

  const byCode = source[normalizedCode];
  if (Array.isArray(byCode)) {
    return byCode;
  }

  const byKey = source[normalizedKey];
  if (Array.isArray(byKey)) {
    return byKey;
  }

  return [];
};

export const resolveReportsSelectorOptions = (payload, localSelectorOptions, selectorCode, key = '') => {
  const localOptions = resolveReportsLocalSelectorOptions(localSelectorOptions, selectorCode, key);

  if (!payload || typeof payload !== 'object') {
    return localOptions;
  }

  const normalizedSelectorCode = normalizeCode(selectorCode);
  if (normalizedSelectorCode === '') {
    return localOptions;
  }

  const selectorOptions = payload.selector_options;
  if (selectorOptions && typeof selectorOptions === 'object') {
    const source = selectorOptions[normalizedSelectorCode];
    if (Array.isArray(source)) {
      return source;
    }

    const extractedSource = extractCollection(source);
    if (extractedSource.length > 0) {
      return extractedSource;
    }

    return localOptions;
  }

  const fallbackSource = payload[normalizedSelectorCode];
  if (Array.isArray(fallbackSource)) {
    return fallbackSource;
  }

  const extractedFallback = extractCollection(fallbackSource);
  if (extractedFallback.length > 0) {
    return extractedFallback;
  }

  return localOptions;
};

export const buildReportsSelectorRequestParams = ({
  reportKey = DEFAULT_REPORTS_TOOLBAR_KEY,
  reportTab = '',
  requestParams = {},
} = {}) => {
  const normalizedReportKey = String(reportKey || '').trim() || DEFAULT_REPORTS_TOOLBAR_KEY;
  const normalizedReportTab = String(reportTab || '').trim();
  const reportParam = normalizedReportKey === DEFAULT_REPORTS_TOOLBAR_KEY
    ? (normalizedReportTab || 'sales_done')
    : normalizedReportKey;

  const params = {
    ...(requestParams && typeof requestParams === 'object' ? requestParams : {}),
    report: reportParam,
  };

  if (normalizedReportTab !== '') {
    params.report_tab = normalizedReportTab;
  }

  return params;
};

export const loadReportsToolbarSelectorContext = async ({
  fetchSelectors,
  reportKey = DEFAULT_REPORTS_TOOLBAR_KEY,
  reportTab = '',
  requestParams = {},
  localSelectorDefinitions = [],
  localSelectorOptions = {},
} = {}) => {
  let payload = {};

  if (typeof fetchSelectors === 'function') {
    try {
      const response = await fetchSelectors(buildReportsSelectorRequestParams({
        reportKey,
        reportTab,
        requestParams,
      }));
      payload = extractPayload(response) || {};
    } catch (error) {
      payload = {};
    }
  }

  const backendDefinitions = normalizeReportsSelectorDefinitions(payload?.selector_definitions || []);
  const normalizedLocalDefinitions = normalizeReportsSelectorDefinitions(localSelectorDefinitions || []);
  const definitions = mergeReportsSelectorDefinitions(backendDefinitions, normalizedLocalDefinitions);

  const optionsByDefinition = definitions.map((definition) => {
    return {
      definition,
      rawOptions: resolveReportsSelectorOptions(payload, localSelectorOptions, definition.code, definition.key),
    };
  });

  return {
    definitions,
    optionsByDefinition,
  };
};
